import { useCallback, useEffect, useMemo, useState } from 'react';
import AppShell from '../components/AppShell';
import { centerPerfAPI } from '../api/client';
import { alertDialog, confirmDialog, toast } from '../components/AppDialog';
import { Page, PageHeader, Button } from '../components/ui';
import { COLORS, TONES, card as uiCard, input as uiInput } from '../utils/uiStyles';
import { IconChartBar } from '@tabler/icons-react';
import { fmtKstDateTime } from '../utils/kstTime';
import RefDataTab from './centerPerf/RefDataTab';
import DashboardTab from './centerPerf/DashboardTab';

/* 센터 실적 대시보드 — 센터 일별출고 엑셀 업로드·저장·조회 + 기준 데이터 + 대시보드(BPO 센터실적 표) (기획팀 1차요청-03)
 * · «대시보드»는 전 센터 조회 대상(/center-perf/me 의 view_all)에게만 있고, 있으면 첫 탭이자 처음 탭이다
 *   (그 밖의 사용자는 이전과 같은 탭·처음 탭).
 * · 단위는 «실적 센터»(BPO 이름 — 오산(저온)·오산(상온)처럼 권한 센터가 같아도 따로)다. /center-perf/me 의
 *   upload_centers·view_centers 의 token = name = 실적 센터명이고, API 의 center 인자도 그 값이다.
 * · 권한은 서버(/center-perf/me)가 정한다 — 업로드 = 실적 센터의 권한 센터가 자기 센터(AX실·관리자·운영 = 전부),
 *   조회 = 업로드 실적 센터 + 열람 조직(처음 값 기획팀)이면 전부, 기준 데이터 = 조회 가능하거나 기준 데이터 관리 조직.
 *   권한이 없으면 안내만 그리고 다른 API 는 부르지 않는다.
 * · «저장»은 같은 실적 센터·같은 파일의 미리보기가 성공한 뒤에만 켠다. 서버는 저장 때 같은 파일을 다시 파싱·검산한다.
 * · 확인 경고(confirms — B2 권한 센터가 다름·직전 업로드와 고객사 겹침 미달·같은 파일이 다른 실적 센터에 있음)는 전부 체크해야
 *   «저장»이 켜지고, 체크한 코드를 함께 보낸다. 서버가 다시 계산해 빠진 경고가 있으면 409 — 그 목록으로 다시 그리고 체크를 비운다.
 * · 고객사별 실적 센터 예외로 다른 실적 센터에 갈 출고는 «실적 센터별 반영» 표(split), 못 정한 고객사는 «실적 센터 확인 필요»(needs_center).
 * · 값이 있는 날짜만 그 실적 센터·날짜를 통째로 교체한다(빈 날짜는 그대로) — 미리보기 표의 «교체/신규» 가 그 범위다.
 * · 소계 검산이 틀린 날짜는 서버가 빼고(excluded — 기존 저장값 그대로) 나머지를 저장한다. 맞는 날짜가 없으면 400 불일치 표.
 *   오늘(KST) 이후 날짜 열은 서버가 읽지 않는다(future_date_count).
 */
const LABOR_KEYS = ['인원_정규', '인원_도급', '시간_정규', '시간_도급'];
const EXCLUDED_LIMIT = 100;     // 서버가 보내는 검산 제외 행 상한(center_perf_parser.MAX_MISMATCHES)
const ORIGIN_LABEL = { manual: '수동', auto: '자동' };

const card = { ...uiCard, padding: '14px', marginBottom: '12px' };
const th = { height: '33px', padding: '5px 9px', textAlign: 'left', fontSize: '12px', fontWeight: 400, color: COLORS.textMuted, whiteSpace: 'nowrap', borderBottom: `1px solid ${COLORS.borderSoft}` };
const td = { height: '41px', padding: '7px 9px', fontSize: '13px', color: COLORS.textSub, borderBottom: `1px solid ${COLORS.borderSoft}`, whiteSpace: 'nowrap' };
// 각 표의 첫 열 — 값은 th/td 와 같고 왼쪽 패딩만 4(시안 .database :first-child). shorthand 와 paddingLeft 를 한 객체에 섞지 않는다.
const thFirst = { textAlign: 'left', height: '33px', paddingTop: '5px', paddingRight: '9px', paddingBottom: '5px', paddingLeft: '4px', borderBottom: `1px solid ${COLORS.borderSoft}`, color: COLORS.textMuted, fontWeight: 400, fontSize: '12px', whiteSpace: 'nowrap' };
const tdFirst = { height: '41px', paddingTop: '7px', paddingRight: '9px', paddingBottom: '7px', paddingLeft: '4px', fontSize: '13px', color: COLORS.text, fontWeight: 500, borderBottom: `1px solid ${COLORS.borderSoft}`, whiteSpace: 'nowrap' };
const tdNum = { ...td, textAlign: 'right' };
const inp = { ...uiInput, width: 'auto' };
// 탭 단추(시안 .view-tab) — 색은 var(--gw-…) 라 다크에서도 따라온다. 단추는 탭 줄의 직접 자식·글자만(탭 줄 자식 글자 목록을 보는 시험).
const pillTab = (on) => ({
  padding: '5px 10px', borderRadius: '18px', fontSize: '13px', border: 0, cursor: 'pointer', whiteSpace: 'nowrap',
  background: on ? `var(--gw-gray-soft, ${TONES.gray.soft})` : 'transparent',
  color: on ? `var(--gw-text, ${COLORS.text})` : `var(--gw-muted, ${COLORS.textMuted})`,
  fontWeight: on ? 500 : 400,
});
const hint = { fontSize: '12px', color: COLORS.textMuted, marginTop: '8px' };
// 띠 글자는 본문색 — dangerText·warn 은 각 바탕 위 대비 4.5 미달. 오류·경고 단서는 바탕색과 문구가 전한다.
const errBox = { marginTop: '10px', padding: '8px 10px', borderRadius: '6px', background: COLORS.dangerSoft, color: COLORS.text, fontSize: '13px' };
const warnBox = { ...errBox, background: COLORS.warnSoft, color: COLORS.text };

const detailOf = (e, fallback) => {
  const d = e?.response?.data?.detail;
  return typeof d === 'string' && d ? d : fallback;
};
// 검산 불일치 400 = detail 이 {message, mismatches} 객체다(그 밖의 400 은 문자열).
const mismatchOf = (e) => {
  const d = e?.response?.data?.detail;
  return d && typeof d === 'object' && Array.isArray(d.mismatches) ? d : null;
};
// 확인 경고 409 = detail 이 {message, confirms} 객체다.
const confirmsOf = (e) => {
  const d = e?.response?.data?.detail;
  return e?.response?.status === 409 && d && typeof d === 'object' && Array.isArray(d.confirms) ? d : null;
};
const fmtNum = (v) => {
  if (v === null || v === undefined || v === '') return '—';
  const n = Number(v);
  return Number.isFinite(n) ? n.toLocaleString('ko-KR') : String(v);
};
const monthEnd = (ym) => {
  const [y, m] = ym.split('-').map(Number);
  return `${ym}-${String(new Date(Date.UTC(y, m, 0)).getUTCDate()).padStart(2, '0')}`;
};

// bg = 선택 행 바탕(ViewTab) — 바탕은 tr 이 아니라 td 에 둔다(.gw-tr:hover>td 에 가려지지 않게)
function LaborCells({ labor, bg }) {
  return LABOR_KEYS.map((k) => <td key={k} data-col={k} style={{ ...tdNum, background: bg }}>{fmtNum(labor ? labor[k] : null)}</td>);
}

function SubtotalRows({ rows }) {
  return (
    <tbody>
      {rows.map((m) => (
        <tr key={`${m.date}-${m.label}`} className="gw-tr">
          <td style={tdFirst}>{m.date}</td>
          <td style={td}>{m.label}</td>
          <td style={tdNum}>{fmtNum(m.subtotal)}</td>
          <td style={tdNum}>{fmtNum(m.computed)}</td>
        </tr>
      ))}
    </tbody>
  );
}

const SUBTOTAL_HEAD = (
  <thead>
    <tr>{['날짜', '구분', '소계', '고객사 합계'].map((h, i) => <th key={h} style={i === 0 ? thFirst : th}>{h}</th>)}</tr>
  </thead>
);

function ExcludedDates({ data }) {
  const count = data.excluded_date_count || 0;
  if (count <= 0) return null;
  const rows = data.excluded || [];
  return (
    <div data-testid="perf-preview-excluded" role="status" style={{ marginTop: '10px' }}>
      <div style={warnBox}>
        {`검산 불일치로 제외한 날짜 ${count}일 — 이 날짜는 저장하지 않고 기존 저장값도 그대로 둡니다. 센터 파일의 소계 수식을 확인해 주세요.`}
        {rows.length >= EXCLUDED_LIMIT ? ` (앞 ${rows.length}건만 표시)` : ''}
      </div>
      <table style={{ borderCollapse: 'collapse', marginTop: '8px' }}>
        {SUBTOTAL_HEAD}
        <SubtotalRows rows={rows} />
      </table>
    </div>
  );
}

// 확인 경고 — 전부 체크해야 «저장»이 켜진다(체크한 코드는 저장 요청에 함께 간다).
function ConfirmList({ confirms, checked, onToggle }) {
  if (!confirms.length) return null;
  return (
    <div data-testid="perf-preview-confirms" role="group" aria-label="확인이 필요한 경고" style={{ ...warnBox, marginTop: '12px' }}>
      <div style={{ fontWeight: 500, marginBottom: '4px' }}>
        {`확인이 필요한 경고 ${confirms.length}건 — 내용을 확인하고 모두 체크해야 저장할 수 있습니다.`}
      </div>
      {confirms.map((c) => (
        <label key={c.code} data-code={c.code} style={{ display: 'flex', gap: '6px', alignItems: 'flex-start', marginTop: '4px', cursor: 'pointer' }}>
          <input type="checkbox" aria-label={`확인: ${c.message}`} checked={checked.includes(c.code)}
            onChange={(e) => onToggle(c.code, e.target.checked)} style={{ marginTop: '2px' }} />
          <span>{c.message}</span>
        </label>
      ))}
    </div>
  );
}

// 고객사별 실적 센터 예외로 고른 실적 센터가 아닌 곳에 갈 출고 — 그 센터는 그 날짜 출고만 통째로 교체한다(근무는 고른 센터에만).
function SplitTable({ split, center }) {
  if (!split || split.length === 0) return null;
  return (
    <div style={{ marginTop: '10px' }}>
      <div style={{ fontSize: '13px', fontWeight: 500, marginBottom: '4px' }}>실적 센터별 반영</div>
      <div style={hint}>{`아래 고객사 출고는 ${center} 이 아니라 표의 실적 센터에 저장되고, 그 실적 센터의 그 날짜 출고를 교체합니다(근무는 ${center} 에만).`}</div>
      <div style={{ overflowX: 'auto' }}>
        <table data-testid="perf-preview-split" style={{ borderCollapse: 'collapse', width: '100%' }}>
          <thead>
            <tr>{['실적 센터', '고객사', '출고 칸', '날짜별 합계'].map((h, i) => <th key={h} style={i === 0 ? thFirst : th}>{h}</th>)}</tr>
          </thead>
          <tbody>
            {split.map((s) => (
              <tr key={s.perf_center} data-center={s.perf_center} className="gw-tr">
                <td style={tdFirst}>{s.perf_center}</td>
                <td data-col="customers" style={{ ...td, whiteSpace: 'normal' }}>
                  {(s.customers || []).map((c) => `${c.raw_name}(${ORIGIN_LABEL[c.origin] || c.origin})`).join(', ')}
                </td>
                <td data-col="shipment_rows" style={tdNum}>{fmtNum(s.shipment_rows)}</td>
                <td data-col="dates" style={{ ...td, whiteSpace: 'normal' }}>
                  {(s.dates || []).map((d) => `${d.date} ${fmtNum(d.total)}(${d.existing ? '교체' : '신규'})`).join(' · ')}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function NeedsCenter({ needs, count, center }) {
  if (!needs || needs.length === 0) return null;
  return (
    <div data-testid="perf-preview-needs-center" style={{ ...warnBox, marginTop: '10px' }}>
      <div style={{ fontWeight: 500 }}>
        {`실적 센터 확인 필요 ${count || needs.length}건 — 고른 실적 센터(${center})에 저장합니다. 기준 데이터 «고객사 실적 센터»에서 예외를 두면 다음 업로드부터 반영됩니다.`}
        {(count || 0) > needs.length ? ` (앞 ${needs.length}건만 표시)` : ''}
      </div>
      <ul style={{ margin: '4px 0 0', paddingLeft: '18px', maxHeight: '180px', overflowY: 'auto' }}>
        {needs.map((n, i) => <li key={`${n.raw_name}-${i}`} data-raw={n.raw_name}>{n.raw_name} — {n.reason}</li>)}
      </ul>
    </div>
  );
}

function PreviewResult({ data }) {
  const warnings = data.warnings || [];
  return (
    <div style={{ marginTop: '12px' }}>
      <div style={{ fontSize: '13px', marginBottom: '6px' }}>
        <b>{data.center_name}</b> · 파일 센터 {data.file_center_raw
          ? data.file_center_raw
          : <span data-testid="perf-preview-no-b2" style={{ color: COLORS.warn }}>B2 없음 — 고른 실적 센터로 저장</span>} ·
        시트 {data.sheet_name} · 반영 {data.value_date_count}일 · 출고 {data.shipment_rows}칸 · 근무 {data.labor_rows}칸
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table data-testid="perf-preview-dates" style={{ borderCollapse: 'collapse', width: '100%' }}>
          <thead>
            <tr>
              {['날짜', '합계', '일반출고', '도착보장', ...LABOR_KEYS, '반영'].map((h, i) => <th key={h} style={i === 0 ? thFirst : th}>{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {(data.dates || []).map((d) => (
              <tr key={d.date} data-date={d.date} className="gw-tr">
                <td style={tdFirst}>{d.date}</td>
                <td data-col="total" style={tdNum}>{fmtNum(d.total)}</td>
                <td data-col="normal" style={tdNum}>{fmtNum(d.normal)}</td>
                <td data-col="guarantee" style={tdNum}>{fmtNum(d.guarantee)}</td>
                <LaborCells labor={d.labor} />
                <td data-col="existing" style={{ ...td, color: d.existing ? COLORS.warn : COLORS.successText, fontWeight: 600 }}>
                  {d.existing ? '교체' : '신규'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <SplitTable split={data.split} center={data.center_name} />
      <NeedsCenter needs={data.needs_center} count={data.needs_center_count} center={data.center_name} />
      <ExcludedDates data={data} />
      {data.future_date_count > 0 && (
        <div data-testid="perf-preview-future" style={hint}>
          {`반영 기간은 오늘(KST ${data.cutoff_date})까지 — 이후 날짜 열 ${data.future_date_count}개는 읽지 않았습니다.`}
        </div>
      )}
      <div data-testid="perf-preview-warnings" style={{ marginTop: '10px', fontSize: '12px' }}>
        <div style={{ fontWeight: 500, color: data.warning_count ? COLORS.warn : COLORS.textSub }}>
          {data.warning_count ? `경고 ${data.warning_count}건 — 아래 칸은 저장하지 않습니다` : '경고 없음'}
          {data.warning_count > warnings.length ? ` (앞 ${warnings.length}건만 표시)` : ''}
        </div>
        {warnings.length > 0 && (
          <ul style={{ margin: '4px 0 0', paddingLeft: '18px', maxHeight: '220px', overflowY: 'auto' }}>
            {warnings.map((w, i) => (
              <li key={`${w.cell || 'row'}-${i}`}>
                {[w.cell, w.date, w.label].filter(Boolean).join(' · ')}
                {w.value !== null && w.value !== undefined ? ` · '${w.value}'` : ''} — {w.reason}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function MismatchTable({ detail }) {
  return (
    <div style={{ marginTop: '10px' }}>
      <div role="alert" style={errBox}>{detail.message} — 검산이 맞는 날짜가 없어 파일 전체를 저장하지 않습니다.</div>
      <table data-testid="perf-preview-mismatch" style={{ borderCollapse: 'collapse', marginTop: '8px' }}>
        {SUBTOTAL_HEAD}
        <SubtotalRows rows={detail.mismatches} />
      </table>
    </div>
  );
}

function UploadTab({ centers }) {
  const [center, setCenter] = useState(centers[0]?.token || '');
  const [file, setFile] = useState(null);
  const [fileKey, setFileKey] = useState(0);        // 저장 뒤 파일 칸을 비운다(input 은 값으로 못 비운다)
  const [preview, setPreview] = useState(null);     // { center, file, data } — 저장은 이 값과 지금 선택이 같을 때만
  const [checked, setChecked] = useState([]);       // 체크한 확인 경고 코드
  const [mismatch, setMismatch] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);

  const clearResult = () => { setPreview(null); setChecked([]); setMismatch(null); setError(''); };

  const loadHistory = useCallback(async () => {
    if (!center) { setHistory([]); return; }
    try {
      const r = await centerPerfAPI.uploads(center);
      setHistory(r.data?.uploads || []);
    } catch (e) {
      setHistory([]);
    }
  }, [center]);
  useEffect(() => { loadHistory(); }, [loadHistory]);

  const runPreview = async () => {
    if (!center || !file) return;
    clearResult();
    setBusy(true);
    try {
      const r = await centerPerfAPI.preview(center, file);
      setPreview({ center, file, data: r.data });
    } catch (e) {
      const m = mismatchOf(e);
      if (m) setMismatch(m);
      else setError(detailOf(e, '미리보기에 실패했습니다.'));
    } finally {
      setBusy(false);
    }
  };

  const confirms = preview?.data?.confirms || [];
  const allChecked = confirms.every((c) => checked.includes(c.code));
  const canSave = !!preview && preview.center === center && preview.file === file && !busy && allChecked;

  const toggle = (code, on) => setChecked((prev) => (on ? [...prev.filter((c) => c !== code), code] : prev.filter((c) => c !== code)));

  const save = async () => {
    if (!canSave) return;
    setBusy(true);
    try {
      const r = await centerPerfAPI.save(center, file, confirms.map((c) => c.code).filter((code) => checked.includes(code)));
      const d = r.data || {};
      const excluded = d.excluded_date_count > 0 ? ` · 검산 불일치 ${d.excluded_date_count}일 제외` : '';
      const split = (d.split || []).map((s) => ` · ${s.perf_center} 출고 ${s.shipment_rows}칸`).join('');
      toast(`저장했습니다 — ${d.value_date_count}일 반영(${d.date_from} ~ ${d.date_to})${excluded}${split}`);
      clearResult();
      setFile(null);
      setFileKey((k) => k + 1);
      loadHistory();
    } catch (e) {
      const c = confirmsOf(e);
      if (c) {
        // 서버가 다시 계산한 경고로 목록을 다시 그리고 체크를 비운다(미리보기·파일은 그대로 — 다시 확인하면 저장된다).
        setPreview((p) => (p ? { ...p, data: { ...p.data, confirms: c.confirms } } : p));
        setChecked([]);
        setError(c.message || '확인이 필요한 경고가 있습니다.');
        return;
      }
      const m = mismatchOf(e);
      clearResult();
      if (m) setMismatch(m);
      else setError(detailOf(e, '저장에 실패했습니다.'));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (u) => {
    const ok = await confirmDialog(
      `'${u.original_name}' 업로드를 지웁니다. 이 업로드가 지금 가진 값(${u.live_date_count}일)이 사라지고, 이전 파일 값으로 되돌리지는 않습니다.`,
      { danger: true });
    if (!ok) return;
    try {
      await centerPerfAPI.removeUpload(u.id);
      toast('삭제했습니다.');
      loadHistory();
    } catch (e) {
      alertDialog(detailOf(e, '삭제하지 못했습니다.'));
    }
  };

  return (
    <>
      <div style={card}>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
          <select aria-label="업로드 센터" value={center} style={{ ...inp, minWidth: '170px' }}
            onChange={(e) => { setCenter(e.target.value); clearResult(); }}>
            {centers.map((c) => <option key={c.token} value={c.token}>{c.name}</option>)}
          </select>
          <input key={fileKey} type="file" aria-label="실적 파일" style={inp}
            accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            onChange={(e) => { setFile((e.target.files && e.target.files[0]) || null); clearResult(); }} />
          <Button variant="secondary" onClick={runPreview} disabled={!center || !file || busy}>미리보기</Button>
          <Button variant="primary" onClick={save} disabled={!canSave}>저장</Button>
        </div>
        <div style={hint}>
          값이 있는 날짜만 그 실적 센터·날짜를 통째로 교체하고, 값이 없는 날짜는 그대로 둡니다.
          빈칸은 저장하지 않고 0 은 0 으로 저장합니다. «저장»은 미리보기가 끝난 뒤에 켜집니다.
          파일 실적요약 B2 가 고른 실적 센터의 권한 센터와 다르거나, 직전 업로드와 고객사가 절반 넘게 다르거나,
          같은 파일이 다른 실적 센터에 있으면 확인 경고를 모두 체크해야 저장됩니다.
          소계가 틀린 날짜만 빼고 저장 · 오늘 이후 날짜 열은 읽지 않음.
        </div>
        {error && <div role="alert" style={errBox}>{error}</div>}
        {mismatch && <MismatchTable detail={mismatch} />}
        {preview && <ConfirmList confirms={confirms} checked={checked} onToggle={toggle} />}
        {preview && <PreviewResult data={preview.data} />}
      </div>

      <div style={card}>
        <div style={{ fontSize: '14px', fontWeight: 620, color: COLORS.text, marginBottom: '8px' }}>업로드 이력</div>
        <div style={{ overflowX: 'auto' }}>
          <table data-testid="perf-upload-history" style={{ borderCollapse: 'collapse', width: '100%' }}>
            <thead>
              <tr>{['올린 시각', '파일명', '업로더', '기간', '반영 날짜(남은/올린)', '경고', '검산 제외', ''].map((h, i) => <th key={h || 'act'} style={i === 0 ? thFirst : th}>{h}</th>)}</tr>
            </thead>
            <tbody>
              {history.length === 0 && (
                <tr><td colSpan={8} style={{ ...td, textAlign: 'center', color: COLORS.textMuted }}>아직 올린 파일이 없습니다.</td></tr>
              )}
              {history.map((u) => (
                <tr key={u.id} data-upload-id={u.id} className="gw-tr">
                  <td style={tdFirst}>{fmtKstDateTime(u.created_at) || '—'}</td>
                  <td style={{ ...td, whiteSpace: 'normal' }}>{u.original_name}</td>
                  <td style={td}>{u.uploader_name}</td>
                  <td style={td}>{u.date_from} ~ {u.date_to}</td>
                  <td style={tdNum}>{u.live_date_count}/{u.value_date_count}</td>
                  <td style={tdNum}>{u.warning_count}</td>
                  <td data-col="excluded" style={{ ...tdNum, color: u.excluded_date_count ? COLORS.warn : undefined }}>{u.excluded_date_count || 0}</td>
                  <td style={td}>
                    <Button variant="danger" onClick={() => remove(u)}>삭제</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

function ViewTab({ centers }) {
  const [center, setCenter] = useState(centers[0]?.token || '');
  const [month, setMonth] = useState('');           // '' = 서버 기본(그 센터 최신 저장일의 달)
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [picked, setPicked] = useState('');

  useEffect(() => {
    if (!center) return undefined;
    let alive = true;
    setError('');
    const opts = month ? { start: `${month}-01`, end: monthEnd(month) } : {};
    centerPerfAPI.data(center, opts)
      .then((r) => { if (alive) { setData(r.data); setPicked(''); } })
      .catch((e) => { if (alive) { setData(null); setError(detailOf(e, '저장 데이터를 불러오지 못했습니다.')); } });
    return () => { alive = false; };
  }, [center, month]);

  const shownMonth = month || (data?.start || '').slice(0, 7);
  const monthOptions = useMemo(
    () => Array.from(new Set([...(data?.months || []), shownMonth].filter(Boolean))).sort().reverse(),
    [data, shownMonth]);
  const dayRows = (data?.rows || []).filter((r) => r.date === picked);
  const dates = data?.dates || [];

  return (
    <div style={card}>
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '10px' }}>
        <select aria-label="조회 센터" value={center} style={{ ...inp, minWidth: '170px' }}
          onChange={(e) => { setCenter(e.target.value); setMonth(''); }}>
          {centers.map((c) => <option key={c.token} value={c.token}>{c.name}</option>)}
        </select>
        <select aria-label="조회 월" value={shownMonth} style={inp} onChange={(e) => setMonth(e.target.value)}>
          {monthOptions.map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
        {data && <span style={{ fontSize: '12px', color: COLORS.textMuted }}>{data.center_name} · {data.start} ~ {data.end}</span>}
      </div>
      {error && <div role="alert" style={errBox}>{error}</div>}
      <div style={{ overflowX: 'auto' }}>
        <table data-testid="perf-view-dates" style={{ borderCollapse: 'collapse', width: '100%' }}>
          <thead>
            <tr>{['날짜', '합계', '일반출고', '도착보장', ...LABOR_KEYS].map((h, i) => <th key={h} style={i === 0 ? thFirst : th}>{h}</th>)}</tr>
          </thead>
          <tbody>
            {data && dates.length === 0 && (
              <tr><td colSpan={8} style={{ ...td, textAlign: 'center', color: COLORS.textMuted }}>이 기간에 저장된 값이 없습니다.</td></tr>
            )}
            {dates.map((d) => {
              // 선택 바탕은 각 td 에 — tr 바탕은 .gw-tr:hover>td 에 가려진다(선택 행은 hover 에도 유지)
              const rowBg = picked === d.date ? COLORS.primarySoft : undefined;
              return (
                <tr key={d.date} data-date={d.date} tabIndex={0} aria-selected={picked === d.date} className="gw-tr"
                  onClick={() => setPicked(d.date)}
                  onKeyDown={(e) => { if (e.key === 'Enter') setPicked(d.date); }}
                  style={{ cursor: 'pointer' }}>
                  <td style={{ ...tdFirst, background: rowBg }}>{d.date}</td>
                  <td data-col="total" style={{ ...tdNum, background: rowBg }}>{fmtNum(d.total)}</td>
                  <td data-col="normal" style={{ ...tdNum, background: rowBg }}>{fmtNum(d.normal)}</td>
                  <td data-col="guarantee" style={{ ...tdNum, background: rowBg }}>{fmtNum(d.guarantee)}</td>
                  <LaborCells labor={d.labor} bg={rowBg} />
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {!picked && dates.length > 0 && <div style={hint}>날짜 행을 누르면 그날 고객사별 출고가 보입니다.</div>}
      {picked && (
        <div style={{ marginTop: '12px' }}>
          <div style={{ fontSize: '13px', fontWeight: 500, marginBottom: '6px' }}>{picked} 고객사별 출고</div>
          <div style={{ overflowX: 'auto' }}>
            <table data-testid="perf-view-rows" style={{ borderCollapse: 'collapse', width: '100%' }}>
              <thead>
                <tr>{['구역', '출고구분', '고객사명', '상세고객사명', '사업자번호', '출고방식', '수량'].map((h, i) => <th key={h} style={i === 0 ? thFirst : th}>{h}</th>)}</tr>
              </thead>
              <tbody>
                {dayRows.length === 0 && (
                  <tr><td colSpan={7} style={{ ...td, textAlign: 'center', color: COLORS.textMuted }}>이 날짜에는 출고 값이 없습니다(근무만 있음).</td></tr>
                )}
                {dayRows.map((r, i) => (
                  <tr key={`${r.zone}-${r.customer_name}-${i}`} className="gw-tr">
                    <td style={tdFirst}>{r.zone}</td>
                    <td style={td}>{r.out_div}</td>
                    <td style={{ ...td, whiteSpace: 'normal' }}>{r.customer_name}</td>
                    <td style={{ ...td, whiteSpace: 'normal' }}>{r.customer_detail}</td>
                    <td style={td}>{r.biz_no}</td>
                    <td style={td}>{r.out_method}</td>
                    <td data-col="qty" style={tdNum}>{fmtNum(r.qty)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default function CenterPerfPage() {
  const [me, setMe] = useState(null);               // null = 불러오는 중
  const [tab, setTab] = useState('');

  useEffect(() => {
    let alive = true;
    centerPerfAPI.me()
      .then((r) => {
        if (!alive) return;
        const m = r.data || { visible: false };
        setMe(m);
        if (m.view_all) setTab('dashboard');
        else setTab(m.can_upload ? 'upload' : (m.can_view ? 'view' : (m.can_manage_ref ? 'ref' : '')));
      })
      .catch(() => { if (alive) setMe({ visible: false }); });
    return () => { alive = false; };
  }, []);

  const tabs = me && me.visible
    ? [
      me.view_all && ['dashboard', '대시보드'],
      me.can_upload && ['upload', '업로드'],
      me.can_view && ['view', '저장 데이터 조회'],
      (me.can_view || me.can_manage_ref) && ['ref', '기준 데이터'],
    ].filter(Boolean)
    : [];

  return (
    <AppShell app="center-perf">
      {/* 🔴 본문은 사이드바의 **형제**다 — AppSidebar 의 children 은 사이드바 아코디언용(CenterDailyPage 와 같은 틀). */}
      <div style={{ flex: 1, minWidth: 0, height: 'var(--gw-app-h, 100dvh)', overflow: 'auto' }}>
        <Page>
          <PageHeader
            icon={<IconChartBar aria-hidden="true" />}
            title="센터 실적 대시보드"
            subtitle={(
              <>
                실적 센터별 일별출고 엑셀(실적요약·일별실적 시트)을 올려 저장하고, 저장된 값을 날짜별로 봅니다.
                {me && me.view_all ? ' «대시보드»는 BPO 센터실적 표(주·월·년 실적·목표·달성률·CAPA)를 기준일·데이터 출처로 보고 엑셀로 받습니다.' : ''}
              </>
            )}
          />

          {me === null && <div style={{ ...card, color: COLORS.textMuted, fontSize: '13px' }}>불러오는 중…</div>}
          {me && !me.visible && (
            <div role="alert" style={{ ...card, color: COLORS.dangerText, fontSize: '13px' }}>
              권한이 없습니다. 센터 소속 직원이나 열람 조직 직원만 볼 수 있습니다.
            </div>
          )}
          {tabs.length > 0 && (
            <>
              <div className="gw-tabs">
                {tabs.map(([k, label]) => (
                  <button key={k} type="button" className="gw-tab" aria-pressed={tab === k} onClick={() => setTab(k)}
                    style={pillTab(tab === k)}>{label}</button>
                ))}
              </div>
              {tab === 'dashboard' && <DashboardTab />}
              {tab === 'upload' && <UploadTab centers={me.upload_centers || []} />}
              {tab === 'view' && <ViewTab centers={me.view_centers || []} />}
              {tab === 'ref' && <RefDataTab />}
            </>
          )}
        </Page>
      </div>
    </AppShell>
  );
}
